/**
 * Verification SPA opened from the SMS link (`#/s/<token>`).
 *
 * Screen flow: intro (consent + step list) → capture (the react-sdk
 * IdVerification component: ID front/back + PDF417, liveness challenges,
 * on-device face match) → submitting (results pushed step-by-step to
 * IdVerify) → done (decision) — with expired/error terminal screens.
 *
 * Biometric posture: liveness and face matching run entirely on-device;
 * only pass/fail + scores and the ID images needed for OCR/barcode decoding
 * are sent to the API, which discards images after extraction.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IdVerification, createFaceApiMatcher } from "@inertiax/kyc-react";
import type { IdVerificationResult } from "@inertiax/kyc-react";
import * as zxing from "@zxing/library";
import {
  API_URL,
  ApiClientError,
  createDemoSession,
  DEMO_MODE,
  tokenFromHash,
  VerifyApi,
  type CompleteResponse,
  type OcrIdFields,
  type SessionView,
  type StepId,
} from "./api";

/** What the confirm screen shows, merged from barcode (authoritative) + OCR. */
interface ConfirmFields {
  name?: string;
  dateOfBirth?: string;
  documentNumber?: string;
  expiryDate?: string;
  address?: string;
}

type Screen =
  | { name: "loading" }
  | { name: "demo" }
  | { name: "intro"; session: SessionView }
  | { name: "capture"; session: SessionView }
  | { name: "submitting"; detail: string }
  | {
      name: "confirm";
      session: SessionView;
      fields: ConfirmFields;
      faceMatch: IdVerificationResult["faceMatch"];
    }
  | { name: "done"; result: CompleteResponse }
  | { name: "expired" }
  | { name: "error"; message: string; retakeSession?: SessionView };

const STEP_LABELS: Record<StepId, string> = {
  liveness: "Quick liveness check (camera)",
  "id-front": "Photo of the front of your ID",
  "id-back": "Scan of the barcode on the back",
  "face-match": "Match your face to your ID photo",
};

/**
 * Customer-facing text for the API's stable decision reason codes (see
 * IdVerify's SessionEndpoints.cs). Codes we don't map are omitted from the list.
 */
const REASON_MESSAGES: Record<string, string> = {
  document_expired: "Your ID appears to be expired.",
  underage: "You don't meet the minimum age requirement.",
  liveness_failed: "We couldn't confirm a live person on camera.",
  liveness_unverified: "We couldn't automatically confirm a live person on camera.",
  face_match_failed: "The photo on your ID didn't match your selfie.",
  face_match_unverified: "We couldn't automatically compare your selfie to your ID photo.",
  expiry_unreadable: "We couldn't read the expiry date on your ID.",
  dob_unreadable: "We couldn't read the date of birth on your ID.",
  name_mismatch: "The name on the front and back of your ID didn't match.",
  dob_mismatch: "The date of birth on the front and back of your ID didn't match.",
  barcode_unverified:
    "We couldn't confirm the barcode on the back of your ID. Retake a clear, well-lit photo of the back.",
  document_number_mismatch:
    "The document number on the front and back of your ID didn't match.",
};

/**
 * Customer-facing copy for error codes the user can actually recover from by
 * retaking. Anything unmapped falls through to the server's own message.
 */
const RECOVERABLE_ERROR_MESSAGES: Record<string, string> = {
  NO_BARCODE_FOUND:
    "We couldn't read the barcode on the back of your ID. Make sure it's flat, well-lit, in focus, and fully inside the frame, then retake the photo.",
  // The liveness challenge is single-use and short-lived: a stale or replayed
  // one has to be re-run from a fresh nonce, never downgraded to a
  // client-asserted pass.
  LIVENESS_NONCE_EXPIRED:
    "Your liveness check took too long and timed out. Please run through it again.",
  LIVENESS_NONCE_USED:
    "That liveness check was already submitted. Please run through it again.",
  LIVENESS_NONCE_INVALID:
    "We couldn't confirm your liveness check. Please run through it again.",
  TIMEOUT: "The connection is too slow right now. Check your signal and try again.",
  NETWORK_ERROR: "We couldn't reach the server. Check your connection and try again.",
};

/**
 * Intro copy tailored to the merchant's configured verification purpose. Age
 * mode reassures the customer only their age is checked (no ID data reaches the
 * requester); id mode uses the identity-check framing. Copy stays neutral about
 * what kind of business sent the link — it may not be a store at all.
 */
function introCopy(session: SessionView): { title: string; description: string } {
  if (session.mode === "age") {
    const limit = session.ageLimit ?? 21;
    return {
      title: "Verify your age",
      description: `We need to confirm you're at least ${limit}. It takes about a minute and happens right here on your phone — whoever requested this only sees a pass or fail, never your ID details.`,
    };
  }
  return {
    title: "Verify your identity",
    description:
      "We need a quick identity check. It takes about a minute and happens right here on your phone.",
  };
}

/** Seconds the approved screen stays up before sending the customer back. */
const AUTO_RETURN_SECONDS = 5;

/**
 * The merchant's return URL, if it is one we're willing to navigate to. The
 * API already validates it at session creation; this re-check keeps anything
 * but http(s) (javascript:, data:) out of `location` regardless.
 */
function safeReturnUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * "Return" button back to the merchant. On approval it also counts down and
 * redirects on its own; for declined/review it waits for a tap so the customer
 * can read the reasons first.
 */
function ReturnToMerchant({ url, auto }: { url: string; auto: boolean }) {
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RETURN_SECONDS);

  useEffect(() => {
    if (!auto) return;
    if (secondsLeft <= 0) {
      window.location.assign(url);
      return;
    }
    const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [auto, secondsLeft, url]);

  return (
    <>
      {auto && (
        <p className="return-countdown" aria-live="polite">
          {secondsLeft > 0 ? `Taking you back in ${secondsLeft}…` : "Taking you back…"}
        </p>
      )}
      <button className="primary" onClick={() => window.location.assign(url)}>
        {auto ? "Continue now" : "Return to where you started"}
      </button>
    </>
  );
}

/** Map reason codes to unique, human-readable lines (drops unknowns/dupes). */
function describeReasons(reasons: string[] | undefined): string[] {
  if (!reasons) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of reasons) {
    // step_missing:<step> and any unmapped codes are internal — skip them.
    const message = REASON_MESSAGES[code];
    if (message && !seen.has(message)) {
      seen.add(message);
      out.push(message);
    }
  }
  return out;
}

/**
 * Liveness verdict from the SDK result: every requested challenge completed
 * and no challenge explicitly flagged as failed in the metadata.
 */
/**
 * `?debug=1` on the verify link (before the `#`) turns on the SDK's debug mode:
 * live pose readouts in the on-screen guidance, plus console logging. For
 * tuning on a real phone only — it shows internal measurements to the user.
 */
const POSE_DEBUG =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("debug") === "1";

/**
 * ID↔selfie matcher: SFace on the face face-api detects and aligns, with
 * face-api's own descriptor as the fallback — the server only accepts "sface"
 * or "neural" scores as identity evidence (the SDK's default geometric matcher
 * compares face shape, which relatives share). Module-level
 * so a "Retake" remount reuses the already-downloaded models; the library
 * itself is only fetched once IdVerification starts preloading it.
 */
const faceMatcher = createFaceApiMatcher({
  faceapi: () => import("@vladmandic/face-api"),
  modelUrl: `${import.meta.env.BASE_URL}models/face-api`,
  // SFace decides (sessions 65–78: genuine 0.48–0.60, mismatches 0.13–0.20); face-api's score is
  // kept as test data for the admin portal's comparison. If SFace can't load or run, face-api
  // decides as before.
  // ⚠ LEGAL SIGN-OFF PENDING: SFace's training data (CASIA-WebFace, VGGFace2, MS-Celeb-1M) has
  // research-only terms. Sandbox only until counsel signs off — see IdVerify's
  // FACE-MATCH-MODEL-LICENSING.md. To switch it off, set `decide: false`.
  sface: {
    ort: () => import("onnxruntime-web/wasm"),
    modelUrl: `${import.meta.env.BASE_URL}models/sface/face_recognition_sface_2021dec_int8bq.onnx`,
    wasmPaths: `${import.meta.env.BASE_URL}models/ort/`,
    decide: true,
  },
});

/** Merge extracted fields for display: barcode wins, OCR fills the gaps. */
function mergeFields(ocr: OcrIdFields, barcode: Record<string, string>): ConfirmFields {
  const name =
    [barcode.firstName ?? ocr.firstName, barcode.middleName, barcode.lastName ?? ocr.lastName]
      .filter(Boolean)
      .join(" ") ||
    ocr.fullName ||
    undefined;
  const address =
    [barcode.addressStreet, barcode.addressCity, barcode.addressState, barcode.addressPostalCode]
      .filter(Boolean)
      .join(", ") || undefined;
  return {
    name,
    dateOfBirth: barcode.dateOfBirth ?? ocr.dateOfBirth,
    documentNumber: barcode.documentNumber ?? ocr.documentNumber,
    expiryDate: barcode.expiryDate ?? ocr.expiryDate,
    address,
  };
}

export default function App() {
  const [screen, setScreen] = useState<Screen>({ name: "loading" });
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  // Bumped on "Retake" so IdVerification remounts with fresh state.
  const [captureKey, setCaptureKey] = useState(0);
  const submittingRef = useRef(false);

  // Move focus to the new screen's landmark on every transition, so screen
  // readers announce it the way they would a page navigation.
  const mainRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    mainRef.current?.focus();
  }, [screen.name]);

  const api = useMemo(() => {
    const token = tokenFromHash(window.location.hash);
    return token ? new VerifyApi(API_URL, token) : null;
  }, []);

  useEffect(() => {
    if (!api) {
      // No token in the URL. In production that means a mangled SMS link; on
      // the local stack there is no SMS at all, so offer to create a session.
      setScreen(
        DEMO_MODE
          ? { name: "demo" }
          : {
              name: "error",
              message:
                "This link is incomplete. Please use the exact link from your text message.",
            },
      );
      return;
    }
    api
      .getSession()
      .then(async (session) => {
        if (session.status === "completed") {
          // Reopened after finishing (refresh, back button, link tapped again).
          // /complete is idempotent on a decided session — it returns the
          // stored decision and reasons — so show that, never an assumed pass.
          setScreen({ name: "done", result: await api.complete() });
        } else {
          setScreen({ name: "intro", session });
        }
      })
      .catch((err) => {
        if (err instanceof ApiClientError && err.code === "SESSION_EXPIRED") {
          setScreen({ name: "expired" });
        } else {
          setScreen({ name: "error", message: (err as Error).message });
        }
      });
  }, [api]);

  const toErrorScreen = useCallback((err: unknown, retakeSession?: SessionView) => {
    if (err instanceof ApiClientError && err.code === "SESSION_EXPIRED") {
      setScreen({ name: "expired" });
      return;
    }
    const message =
      (err instanceof ApiClientError ? RECOVERABLE_ERROR_MESSAGES[err.code] : undefined) ??
      (err as Error).message;
    setScreen({ name: "error", message, retakeSession });
  }, []);

  /**
   * Demo mode only: mint a session from the browser and re-enter through the
   * normal `#/s/<token>` route, so a demo run exercises exactly the same code
   * path as a real SMS link rather than a parallel one.
   */
  const startDemo = useCallback(async (steps?: StepId[]) => {
    setDemoBusy(true);
    setDemoError(null);
    try {
      const token = await createDemoSession(steps);
      window.location.hash = `#/s/${encodeURIComponent(token)}`;
      window.location.reload();
    } catch (err) {
      setDemoBusy(false);
      setDemoError(
        err instanceof ApiClientError
          ? `${err.message} (${err.code})`
          : ((err as Error).message ?? "Could not start the demo session."),
      );
    }
  }, []);

  /**
   * Ask IdVerify for a single-use liveness nonce (doc HIGH-ASSURANCE §5).
   * `IdVerification` calls this between the document and liveness phases so the
   * nonce's 5-minute TTL isn't spent on ID capture.
   *
   * If the challenge can't be issued (offline, or a session without the
   * liveness step) we resolve `undefined`: liveness still runs, just unbound,
   * and handleCaptureComplete then sends the customer back to retake - an
   * unbound run has no server-verifiable evidence and nowhere to be recorded.
   */
  const requestLivenessNonce = useCallback(async (): Promise<string | undefined> => {
    if (!api) return undefined;
    try {
      return (await api.getLivenessChallenge()).nonce;
    } catch {
      return undefined;
    }
  }, [api]);

  /** Phase 2: after the user confirms their details — face match + complete. */
  const finishVerification = useCallback(
    async (session: SessionView, faceMatch: IdVerificationResult["faceMatch"]) => {
      if (!api || submittingRef.current) return;
      submittingRef.current = true;
      try {
        if (session.steps.includes("face-match")) {
          setScreen({ name: "submitting", detail: "Confirming it's you…" });
          // Method + flags let the server log say *why* a score is 0 (e.g.
          // "id-no-face" = the comparison never ran).
          await api.postFaceMatch(faceMatch.score ?? 0, {
            method: faceMatch.method,
            flags: faceMatch.flags,
            shadow: faceMatch.shadow,
          });
          // Best effort, like the ID photos - the decision does not depend on it.
          if (faceMatch.selfieBlob) await api.postSelfie(faceMatch.selfieBlob).catch(() => {});
        }
        setScreen({ name: "submitting", detail: "Finishing up…" });
        const done = await api.complete();
        setScreen({ name: "done", result: done });
      } catch (err) {
        submittingRef.current = false;
        toErrorScreen(err);
      }
    },
    [api, toErrorScreen],
  );

  /**
   * Phase 1: push the capture results (liveness + both ID sides). Step
   * results may be re-posted until /complete, so "Retake" simply runs this
   * again and overwrites. Ends on the confirm screen when there are document
   * fields to review, otherwise finishes immediately.
   */
  const handleCaptureComplete = useCallback(
    async (result: IdVerificationResult, session: SessionView) => {
      if (!api || submittingRef.current) return;
      submittingRef.current = true;
      const has = (step: StepId) => session.steps.includes(step);

      try {
        if (has("liveness")) {
          setScreen({ name: "submitting", detail: "Checking liveness…" });
          const evidence = result.liveness.metadata.livenessEvidence;
          // No evidence means no nonce was issued (the challenge request failed), so the run
          // was unbound. There is no client-asserted fallback any more - IdVerify removed
          // /steps/liveness because anyone holding the link could post {passed:true} to it -
          // so send the customer back to retake, which requests a fresh challenge.
          if (!evidence) {
            throw new Error(
              "We couldn't start the liveness check. Check your connection and try again.",
            );
          }
          // Nonce-bound path (doc HIGH-ASSURANCE §5/§6): the server re-derives the
          // challenge from its own nonce and recomputes the flash correlation
          // from the raw samples, so its verdict — not the client's — is what
          // lands on the session. A failed verdict is recorded and surfaces as
          // a decline at /complete; we don't tell the user which check failed.
          await api.verifyLiveness(evidence);
        }

        let ocrFields: OcrIdFields = {};
        let barcodeFields: Record<string, string> = {};

        if (has("id-front")) {
          setScreen({ name: "submitting", detail: "Reading your ID…" });
          if (!result.id.frontBlob) throw new Error("The front of your ID was not captured.");
          ocrFields = (await api.postIdFront(result.id.frontBlob)).fields;
        }

        if (has("id-back")) {
          setScreen({ name: "submitting", detail: "Checking the barcode…" });
          if (!result.id.backBlob) throw new Error("The back of your ID was not captured.");
          // The photo always goes up: IdVerify decodes it itself, and uses the on-device
          // decode only when it can't (then the front's OCR must agree with it).
          barcodeFields = (await api.postIdBack(result.id.backBlob, result.id.pdf417Raw ?? undefined)).fields;
        }

        submittingRef.current = false;

        if (has("id-front") || has("id-back")) {
          setScreen({
            name: "confirm",
            session,
            fields: mergeFields(ocrFields, barcodeFields),
            faceMatch: result.faceMatch,
          });
        } else {
          await finishVerification(session, result.faceMatch);
        }
      } catch (err) {
        submittingRef.current = false;
        toErrorScreen(err, session);
      }
    },
    [api, toErrorScreen, finishVerification],
  );

  switch (screen.name) {
    case "loading":
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <div className="center" role="status" aria-live="polite">
            <div className="spinner" aria-hidden="true" />
            <p>Loading your verification…</p>
          </div>
        </main>
      );

    case "demo":
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <h1>Demo verification</h1>
          <p>
            This is the page a customer would open from their text message. The
            local stack doesn't send one, so start a session here instead — this
            screen stands in for the merchant backend that would normally create
            it.
          </p>
          <div className="card">
            <ul className="steps-list">
              <li>
                <span className="dot" aria-hidden="true" />
                Quick: the camera liveness check only
              </li>
              <li>
                <span className="dot" aria-hidden="true" />
                Full: liveness, both sides of an ID, and a face match
              </li>
            </ul>
          </div>
          <p className="fine-print">
            The full flow needs a real driver's licence for the PDF417 barcode on
            the back, and usually ends in <strong>review</strong> because the
            local stack returns fake ID data that won't match your face. The
            quick flow ends in <strong>approved</strong>.
          </p>
          <button
            className="primary"
            disabled={demoBusy}
            onClick={() => void startDemo(["liveness"])}
          >
            {demoBusy ? "Starting…" : "Quick demo — camera only"}
          </button>
          <button
            className="secondary"
            disabled={demoBusy}
            onClick={() => void startDemo()}
          >
            Full demo — ID photos + face match
          </button>
          {demoError && (
            <p className="error-text" role="alert">
              {demoError}
            </p>
          )}
          <p className="fine-print" style={{ opacity: 0.5, marginTop: 6 }}>
            build {__BUILD_ID__} · api {API_URL || "(same origin)"}
          </p>
        </main>
      );

    case "intro": {
      const copy = introCopy(screen.session);
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <h1>{copy.title}</h1>
          <p>{copy.description}</p>
          <div className="card">
            <ul className="steps-list">
              {screen.session.steps.map((step) => (
                <li key={step}>
                  <span
                    className={
                      screen.session.completedSteps.includes(step) ? "dot done" : "dot"
                    }
                    aria-hidden="true"
                  />
                  {STEP_LABELS[step]}
                </li>
              ))}
            </ul>
          </div>
          <p className="fine-print">
            Your face comparison runs on this device and is never uploaded. ID images are used
            only to read your document details, then discarded. Link sent to{" "}
            {screen.session.phoneMasked}.
          </p>
          <p className="fine-print" style={{ opacity: 0.5, marginTop: 6 }}>
            build {__BUILD_ID__}
          </p>
          <button
            className="primary"
            onClick={() => setScreen({ name: "capture", session: screen.session })}
          >
            Start verification
          </button>
        </main>
      );
    }

    case "capture": {
      const steps = screen.session.steps;
      const includeIdCapture = steps.includes("id-front") || steps.includes("id-back");
      // Barcode-only sessions (steps: ["id-back"]) skip straight to the back
      // of the ID - no front photo, no camera liveness or selfie at all.
      const barcodeOnly = steps.includes("id-back") && !steps.includes("id-front");
      return (
        <main ref={mainRef} tabIndex={-1} className="shell full-bleed">
          <div className="verify-stage" role="region" aria-label="ID and liveness capture">
            <IdVerification
              key={captureKey}
              steps={screen.session.livenessChallenges}
              includeIdCapture={includeIdCapture}
              includeLiveness={steps.includes("liveness")}
              idCaptureOptions={{
                zxing,
                startSide: barcodeOnly ? "back" : "front",
                // Front-only sessions (steps include "id-front" but not "id-back") stop after the front photo.
                captureBack: steps.includes("id-back"),
              }}
              faceMatcher={faceMatcher}
              // The server recomputes the verdict from the evidence bundle, so
              // the client must not gate on its own fused score — it would turn
              // a server-side decline into a dead-end error screen here.
              livenessOptions={{ enforceLivenessVerdict: false, debug: POSE_DEBUG }}
              requestLivenessNonce={requestLivenessNonce}
              onComplete={(result) => void handleCaptureComplete(result, screen.session)}
              onError={(err) => setScreen({ name: "error", message: err.message })}
            />
          </div>
        </main>
      );
    }

    case "submitting":
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <div className="center" role="status" aria-live="polite">
            <div className="spinner" aria-hidden="true" />
            <p>{screen.detail}</p>
          </div>
        </main>
      );

    case "confirm": {
      const rows: Array<[string, string | undefined]> = [
        ["Name", screen.fields.name],
        ["Date of birth", screen.fields.dateOfBirth],
        ["Document number", screen.fields.documentNumber],
        ["Expires", screen.fields.expiryDate],
        ["Address", screen.fields.address],
      ];
      const visible = rows.filter(([, value]) => Boolean(value));
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <h1>Confirm your details</h1>
          <p>Here's what we read from your ID. Make sure it matches your document.</p>
          <div className="card">
            {visible.length > 0 ? (
              <dl className="details-list">
                {visible.map(([label, value]) => (
                  <div className="details-row" key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p>
                We couldn't read the details clearly. You can retake the photos or continue —
                unclear documents are sent for manual review.
              </p>
            )}
          </div>
          <p className="fine-print">
            Something look wrong? Retaking your ID photos usually fixes misreads from glare or
            blur.
          </p>
          <button
            className="primary"
            onClick={() => void finishVerification(screen.session, screen.faceMatch)}
          >
            Looks right — finish
          </button>
          <button
            className="secondary"
            onClick={() => {
              setCaptureKey((k) => k + 1);
              setScreen({ name: "capture", session: screen.session });
            }}
          >
            Retake ID photos
          </button>
        </main>
      );
    }

    case "done": {
      const { decision } = screen.result;
      const reasonMessages =
        decision === "approved" ? [] : describeReasons(screen.result.reasons);
      const returnUrl = safeReturnUrl(screen.result.redirectUrl);
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <div className="center">
            <span className={`badge ${decision}`}>{decision}</span>
            <h1>
              {decision === "approved"
                ? "You're verified!"
                : decision === "review"
                  ? "Almost there"
                  : "Verification unsuccessful"}
            </h1>
            <p>
              {decision === "approved"
                ? returnUrl
                  ? "Thanks — we'll take you back to where you started."
                  : "You can close this page and return to where you started."
                : decision === "review"
                  ? "We couldn't confirm everything automatically, so your verification has been sent for review."
                  : "We couldn't verify your identity. Please return to where you started for next steps."}
            </p>
            {reasonMessages.length > 0 && (
              <div className="card reasons-card">
                <p className="reasons-title">
                  {decision === "declined" ? "Why it didn't pass" : "What needs a look"}
                </p>
                <ul className="reasons-list">
                  {reasonMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {returnUrl && <ReturnToMerchant url={returnUrl} auto={decision === "approved"} />}
        </main>
      );
    }

    case "expired":
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <div className="center">
            <h1>Link expired</h1>
            <p>
              This verification link is no longer valid. Please request a new one from whoever
              sent it.
            </p>
          </div>
        </main>
      );

    case "error": {
      const retakeSession = screen.retakeSession;
      return (
        <main ref={mainRef} tabIndex={-1} className="shell">
          <div className="center">
            <h1>Something went wrong</h1>
            <p className="error-text" role="alert">
              {screen.message}
            </p>
            {!retakeSession && (
              <p>Reload the page to try again, or request a new link from whoever sent it.</p>
            )}
          </div>
          {retakeSession && (
            <button
              className="primary"
              onClick={() => {
                setCaptureKey((k) => k + 1);
                setScreen({ name: "capture", session: retakeSession });
              }}
            >
              Retake photos
            </button>
          )}
        </main>
      );
    }
  }
}
